import { __decorate, __metadata } from "tslib";
import { CacheActivePromise } from '@1inch-community/core/decorators';
import { getMobileMatchMedia } from '@1inch-community/core/lit-utils';
import { OverlayViewMode } from '@1inch-community/models';
import { OverlayDesktopController } from './overlay-desktop-controller';
import { OverlayMobileController } from './overlay-mobile-controller';
import { OverlayPopupController } from './overlay-popup-controller';
import { viewConfigDefault } from './overlay-view-config-default';
function CacheActivePromiseSerializer(openTarget, viewConfig) {
    let openTargetString = '';
    if (openTarget instanceof HTMLElement) {
        openTargetString = openTarget.outerHTML;
    }
    if ('_$litType$' in openTarget) {
        for (let i = 0; i < openTarget.strings.length; i++) {
            const string = openTarget.strings[i];
            const value = openTarget.values[i];
            openTargetString += string;
            openTargetString += CacheActivePromiseValueSerializer(value);
        }
    }
    return [openTargetString, viewConfig?.mode].join(':');
}
function CacheActivePromiseValueSerializer(value) {
    const valueString = safeStringify(value);
    if (typeof value === 'object' && valueString === null) {
        return value.constructor.name;
    }
    if (typeof value === 'function' && valueString === null) {
        return value.toString();
    }
    return valueString ?? '';
}
function safeStringify(value) {
    try {
        return JSON.stringify(value) ?? null;
    }
    catch {
        return null;
    }
}
export class OverlayController {
    constructor(rootNodeName) {
        this.mobileMedia = getMobileMatchMedia();
        this.mobileOverlay = new OverlayMobileController(rootNodeName);
        this.desktopOverlay = new OverlayDesktopController();
        this.popupOverlay = new OverlayPopupController(rootNodeName);
    }
    async init() { }
    isOpenOverlay(overlayId) {
        if (typeof overlayId !== 'number')
            return false;
        const isOpenDesktopOverlay = this.desktopOverlay.isOpenOverlay(overlayId);
        const isOpenMobileOverlay = this.mobileOverlay.isOpenOverlay(overlayId);
        const isOpenPopupOverlay = this.popupOverlay.isOpenOverlay(overlayId);
        return isOpenDesktopOverlay || isOpenMobileOverlay || isOpenPopupOverlay;
    }
    async open(openTarget, viewConfig = viewConfigDefault) {
        const internalViewConfig = { ...viewConfigDefault, ...viewConfig };
        const [overlay, mode] = this.resolveControllerByMode(internalViewConfig.mode);
        return await overlay.open(openTarget, { ...viewConfig, mode: mode });
    }
    async close(overlayId) {
        if (this.desktopOverlay.isOpenOverlay(overlayId)) {
            return await this.desktopOverlay.close(overlayId);
        }
        if (this.mobileOverlay.isOpenOverlay(overlayId)) {
            return await this.mobileOverlay.close(overlayId);
        }
        if (this.popupOverlay.isOpenOverlay(overlayId)) {
            return await this.popupOverlay.close(overlayId);
        }
    }
    resolveControllerByMode(mode) {
        let resolvedMode = mode;
        if (mode === OverlayViewMode.auto) {
            resolvedMode = this.mobileMedia.matches ? OverlayViewMode.mobile : OverlayViewMode.desktop;
        }
        if (mode === OverlayViewMode.popupAuto) {
            resolvedMode = this.mobileMedia.matches ? OverlayViewMode.mobile : OverlayViewMode.popup;
        }
        let overlay;
        switch (resolvedMode) {
            case OverlayViewMode.desktop:
                overlay = this.desktopOverlay;
                break;
            case OverlayViewMode.mobile:
                overlay = this.mobileOverlay;
                break;
            case OverlayViewMode.popup:
                overlay = this.popupOverlay;
                break;
            default:
                throw new Error(`Unsupported overlay mode: ${resolvedMode}`);
        }
        return [overlay, resolvedMode];
    }
}
__decorate([
    CacheActivePromise(CacheActivePromiseSerializer),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Object]),
    __metadata("design:returntype", Promise)
], OverlayController.prototype, "open", null);
__decorate([
    CacheActivePromise(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number]),
    __metadata("design:returntype", Promise)
], OverlayController.prototype, "close", null);
//# sourceMappingURL=overlay-controller.js.map